import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, LogOut, Building2, Users, DollarSign, Activity, Plus, X, TicketCheck, ShieldCheck, AlertCircle, CheckCircle2, Pencil, Sparkles, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import CuisineTagInput from "../components/CuisineTagInput";

export default function PlatformAdmin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingRid, setEditingRid] = useState(null);
  const [kycRid, setKycRid] = useState(null);

  const load = async () => {
    try {
      const [d, r, t] = await Promise.all([
        api.get("/platform/dashboard"),
        api.get("/platform/restaurants"),
        api.get("/platform/tickets"),
      ]);
      setDash(d.data); setRestaurants(r.data); setTickets(t.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const toggleSubscription = async (r) => {
    try {
      await api.put(`/platform/restaurants/${r.id}/subscription`, {
        tier: r.subscription_tier, active: !r.subscription_active,
      });
      toast.success(r.subscription_active ? "Subscription paused" : "Subscription activated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const resolveTicket = async (t) => {
    try { await api.put(`/platform/tickets/${t.id}`, { status: "resolved" }); toast.success("Resolved"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">God's Eye · Platform</div>
              <div className="font-display text-xl">Mnu<span className="gold-text">Verse</span> HQ</div>
            </div>
          </div>
          <button onClick={logout} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5" data-testid="platform-logout"><LogOut className="w-3.5 h-3.5 inline mr-1" /> Sign out</button>
        </div>

        {dash && (
          <div className="max-w-7xl mx-auto px-5 pb-5 grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Restaurants" value={dash.total_restaurants} sub={`${dash.active_restaurants} active`} icon={<Building2 className="w-4 h-4" />} />
            <KPI label="Total users" value={dash.total_users} icon={<Users className="w-4 h-4" />} />
            <KPI label="Gross revenue" value={`₹${dash.gross_revenue?.toFixed?.(0) || 0}`} icon={<DollarSign className="w-4 h-4" />} />
            <KPI label="Orders today" value={dash.orders_today} icon={<Activity className="w-4 h-4" />} />
            <KPI label="API p50" value={`${dash.avg_api_ms}ms`} sub={`${dash.uptime}% uptime`} icon={<Activity className="w-4 h-4" />} />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-5 pb-3 flex gap-2">
          <Tab active={tab === "overview"} onClick={() => setTab("overview")} label="Tenants" testid="tab-tenants" />
          <Tab active={tab === "ai-usage"} onClick={() => setTab("ai-usage")} label="AI Usage" testid="tab-ai-usage" />
          <Tab active={tab === "tickets"} onClick={() => setTab("tickets")} label={`Support tickets (${tickets.filter((t) => t.status === "open").length})`} testid="tab-tickets" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-6">
        {tab === "ai-usage" && <AIUsagePanel />}        {tab === "overview" && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">Restaurants</h2>
              <button onClick={() => setCreating(true)} data-testid="add-tenant-btn" className="gold-btn rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Onboard restaurant</button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="restaurants-table">
                <thead className="text-[11px] font-mono uppercase tracking-widest text-white/50">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-3">Name</th>
                    <th className="text-left py-2 px-3">Slug</th>
                    <th className="text-left py-2 px-3">Tier</th>
                    <th className="text-left py-2 px-3">KYC</th>
                    <th className="text-right py-2 px-3">Status</th>
                    <th className="text-right py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {restaurants.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`tenant-${r.id}`}>
                      <td className="py-3 px-3"><Link to={`/r/${r.slug}`} className="gold-text hover:underline">{r.name}</Link><div className="text-[10px] text-white/50">{r.cuisine}</div></td>
                      <td className="py-3 px-3 font-mono text-white/60">/r/{r.slug}</td>
                      <td className="py-3 px-3"><span className="text-xs px-2 py-1 rounded-full bg-[#E5A93C]/15 text-[#E5A93C] font-mono uppercase tracking-widest">{r.subscription_tier}</span></td>
                      <td className="py-3 px-3">
                        <button onClick={() => setKycRid(r.id)} data-testid={`kyc-${r.id}`} className="text-xs inline-flex items-center gap-1 hover:underline">
                          <KycBadge status={r.kyc_status} /> {r.kyc_status || "pending"}
                        </button>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <button onClick={() => toggleSubscription(r)} data-testid={`toggle-sub-${r.id}`} className={`text-xs px-3 py-1 rounded-full font-semibold ${r.subscription_active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-red-500/15 text-red-300 border border-red-500/30"}`}>
                          {r.subscription_active ? "Active" : "Paused"}
                        </button>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <button onClick={() => setEditingRid(r.id)} data-testid={`edit-tenant-${r.id}`} className="text-xs inline-flex items-center gap-1 px-3 py-1 rounded-full hover:bg-white/5 border border-white/10">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "tickets" && (
          <section>
            <h2 className="font-display text-2xl mb-4">Global Support Tickets</h2>
            <div className="space-y-2" data-testid="tickets-list">
              {tickets.length === 0 && <div className="text-white/40">No tickets.</div>}
              {tickets.map((t) => (
                <div key={t.id} className={`p-4 rounded-2xl border bg-[#121418] flex items-start justify-between gap-4 ${t.status === "open" ? "border-amber-500/30" : "border-white/10"}`}>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{new Date(t.created_at).toLocaleString()}</div>
                    <div className="font-display text-lg">{t.subject}</div>
                    <div className="text-sm text-white/60 mt-1">{t.description}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-mono uppercase tracking-widest ${t.status === "open" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{t.status}</span>
                    {t.status === "open" && (
                      <button onClick={() => resolveTicket(t)} data-testid={`resolve-${t.id}`} className="text-xs gold-btn rounded-full px-3 py-1 inline-flex items-center gap-1"><TicketCheck className="w-3 h-3" /> Resolve</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {creating && <RestaurantForm onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {editingRid && <EditRestaurantModal rid={editingRid} onClose={() => setEditingRid(null)} onSaved={() => { setEditingRid(null); load(); }} />}
      {kycRid && <KycModal rid={kycRid} onClose={() => setKycRid(null)} onSaved={() => { setKycRid(null); load(); }} />}
    </div>
  );
}

/* ---------- AI Usage panel (per-restaurant totals + editable monthly limit) ---------- */
function AIUsagePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRid, setEditingRid] = useState(null);
  const [draftLimit, setDraftLimit] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/llm-usage");
      setData(data);
    } catch { toast.error("Could not load AI usage"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (r) => {
    setEditingRid(r.restaurant_id);
    setDraftLimit(r.limit_monthly != null ? String(r.limit_monthly) : "");
  };

  const saveLimit = async (r) => {
    const parsed = draftLimit.trim() === "" ? null : Number(draftLimit);
    if (parsed != null && (Number.isNaN(parsed) || parsed < 0)) {
      toast.error("Enter a non-negative number (or leave blank for unlimited)");
      return;
    }
    try {
      await api.put(`/platform/restaurants/${r.restaurant_id}/llm-limit`, { limit: parsed });
      toast.success(parsed == null ? `${r.name} is now unlimited` : `${r.name} capped at ${parsed.toLocaleString()} tokens/month`);
      setEditingRid(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update limit"); }
  };

  const fmt = (n) => (n || 0).toLocaleString();

  const sumPlatform = data?.restaurants?.reduce(
    (acc, r) => {
      acc.inputMonth += r.this_month?.input || 0;
      acc.outputMonth += r.this_month?.output || 0;
      acc.inputLife += r.lifetime?.input || 0;
      acc.outputLife += r.lifetime?.output || 0;
      acc.calls += r.lifetime?.calls || 0;
      return acc;
    },
    { inputMonth: 0, outputMonth: 0, inputLife: 0, outputLife: 0, calls: 0 }
  );

  return (
    <section data-testid="ai-usage-panel">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E5A93C]" /> AI Sommelier usage — by restaurant
          </h2>
          <p className="text-xs text-white/60 mt-1 max-w-xl">
            Token counts are reported directly by Anthropic for every chat. Set a monthly cap per restaurant — once exceeded, new guest chats at that tenant fall back to the offline recommender.
          </p>
        </div>
      </div>

      {sumPlatform && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <PlatformStat label="Platform input (this month)" value={fmt(sumPlatform.inputMonth)} testid="platform-input-month" />
          <PlatformStat label="Platform output (this month)" value={fmt(sumPlatform.outputMonth)} testid="platform-output-month" />
          <PlatformStat label="Lifetime tokens" value={fmt(sumPlatform.inputLife + sumPlatform.outputLife)} testid="platform-lifetime" />
          <PlatformStat label="Lifetime chats" value={fmt(sumPlatform.calls)} testid="platform-calls" />
        </div>
      )}

      {loading && <p className="text-white/60">Loading…</p>}

      {!loading && data && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="ai-usage-table">
            <thead className="text-[11px] font-mono uppercase tracking-widest text-white/50">
              <tr className="border-b border-white/10">
                <th className="text-left py-2 px-3">Restaurant</th>
                <th className="text-left py-2 px-3">Tier</th>
                <th className="text-right py-2 px-3">Month in</th>
                <th className="text-right py-2 px-3">Month out</th>
                <th className="text-right py-2 px-3">Lifetime total</th>
                <th className="text-right py-2 px-3">Chats</th>
                <th className="text-left py-2 px-3">Monthly cap</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.restaurants.map((r) => {
                const monthTotal = (r.this_month?.input || 0) + (r.this_month?.output || 0);
                const overCap = r.limit_monthly != null && monthTotal >= r.limit_monthly;
                const nearCap = r.limit_monthly != null && monthTotal >= r.limit_monthly * 0.8;
                return (
                  <tr key={r.restaurant_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`usage-row-${r.slug}`}>
                    <td className="py-3 px-3">
                      <Link to={`/r/${r.slug}`} className="gold-text hover:underline">{r.name}</Link>
                      <div className="text-[10px] text-white/40 font-mono">/r/{r.slug}</div>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-[#E5A93C]/15 text-[#E5A93C] font-mono uppercase tracking-widest">{r.subscription_tier || "—"}</span>
                    </td>
                    <td className="py-3 px-3 text-right font-mono">{fmt(r.this_month?.input)}</td>
                    <td className="py-3 px-3 text-right font-mono">{fmt(r.this_month?.output)}</td>
                    <td className="py-3 px-3 text-right font-mono">{fmt((r.lifetime?.input || 0) + (r.lifetime?.output || 0))}</td>
                    <td className="py-3 px-3 text-right font-mono">{fmt(r.lifetime?.calls)}</td>
                    <td className="py-3 px-3">
                      {editingRid === r.restaurant_id ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={draftLimit}
                            onChange={(e) => setDraftLimit(e.target.value)}
                            placeholder="unlimited"
                            data-testid={`limit-input-${r.slug}`}
                            className="w-28 bg-[#0A0A0C] border border-white/15 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-[#E5A93C]"
                            type="number"
                            min={0}
                          />
                          <button onClick={() => saveLimit(r)} data-testid={`limit-save-${r.slug}`} className="gold-btn text-xs rounded-full px-2 py-1 font-semibold">Save</button>
                          <button onClick={() => setEditingRid(null)} className="text-xs text-white/50 hover:text-white px-1">✕</button>
                        </div>
                      ) : r.limit_monthly == null ? (
                        <span className="inline-flex items-center gap-1 text-xs text-white/50" data-testid={`limit-unlim-${r.slug}`}>
                          <InfinityIcon className="w-3.5 h-3.5" /> Unlimited
                        </span>
                      ) : (
                        <div className="flex flex-col gap-0.5" data-testid={`limit-val-${r.slug}`}>
                          <span className={`text-xs font-mono ${overCap ? "text-red-400" : nearCap ? "text-amber-300" : "text-white/80"}`}>
                            {fmt(monthTotal)} / {fmt(r.limit_monthly)}
                          </span>
                          <div className="h-1 w-28 rounded-full bg-white/5 overflow-hidden">
                            <div
                              className="h-full"
                              style={{
                                width: `${Math.min(100, (monthTotal / r.limit_monthly) * 100)}%`,
                                background: overCap ? "rgb(239,68,68)" : nearCap ? "rgb(234,179,8)" : "#E5A93C",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right">
                      {editingRid !== r.restaurant_id && (
                        <button onClick={() => startEdit(r)} data-testid={`edit-limit-${r.slug}`} className="text-xs inline-flex items-center gap-1 px-3 py-1 rounded-full hover:bg-white/5 border border-white/10">
                          <Pencil className="w-3 h-3" /> Cap
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PlatformStat({ label, value, testid }) {
  return (
    <div className="p-4 rounded-2xl border border-white/10 bg-[#121418]" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}


function KycBadge({ status }) {
  const s = status || "pending";
  const map = {
    verified: { Icon: CheckCircle2, cls: "text-emerald-400" },
    under_review: { Icon: ShieldCheck, cls: "text-blue-400" },
    rejected: { Icon: AlertCircle, cls: "text-red-400" },
    pending: { Icon: AlertCircle, cls: "text-amber-400" },
  };
  const { Icon, cls } = map[s] || map.pending;
  return <Icon className={`w-3.5 h-3.5 inline mr-1 ${cls}`} />;
}

function KycModal({ rid, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    gst: "", fssai: "", pan: "",
    owner_name: "", owner_id_proof: "",
    bank_account: "", ifsc: "",
    address_proof: "",
    status: "pending", note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/platform/restaurants/${rid}/kyc`);
        setData(data);
        const d = data.kyc_docs || {};
        setForm({
          gst: d.gst || "", fssai: d.fssai || "", pan: d.pan || "",
          owner_name: d.owner_name || "", owner_id_proof: d.owner_id_proof || "",
          bank_account: d.bank_account || "", ifsc: d.ifsc || "",
          address_proof: d.address_proof || "",
          status: data.kyc_status || "pending",
          note: data.kyc_note || "",
        });
      } catch { toast.error("Could not load KYC"); onClose(); }
    })();
  }, [rid, onClose]);

  const save = async (newStatus) => {
    setSaving(true);
    try {
      const { gst, fssai, pan, owner_name, owner_id_proof, bank_account, ifsc, address_proof } = form;
      await api.put(`/platform/restaurants/${rid}/kyc`, {
        docs: { gst, fssai, pan, owner_name, owner_id_proof, bank_account, ifsc, address_proof },
        status: newStatus || form.status,
        note: form.note,
      });
      toast.success(newStatus === "verified" ? "KYC verified" : newStatus === "rejected" ? "KYC rejected" : "KYC saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  if (!data) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-[#0F1115] rounded-3xl border border-white/10 p-6 max-h-[92vh] overflow-y-auto" data-testid="kyc-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">KYC & Compliance</div>
            <h3 className="font-display text-2xl">{data.name}</h3>
            <div className="text-xs text-white/50 mt-1 flex items-center gap-2">
              <KycBadge status={data.kyc_status} /> Current status: <span className="font-semibold">{data.kyc_status || "pending"}</span>
              {data.kyc_updated_at && <span className="font-mono">· {new Date(data.kyc_updated_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full glass-light flex items-center justify-center" data-testid="close-kyc"><X className="w-4 h-4" /></button>
        </div>

        <div className="mt-5 space-y-3">
          <Section title="Business tax IDs">
            <div className="grid sm:grid-cols-2 gap-3">
              <KycInput label="GST Number" value={form.gst} onChange={(v) => setForm({ ...form, gst: v })} testid="kyc-gst" placeholder="29ABCDE1234F1Z5" />
              <KycInput label="FSSAI License" value={form.fssai} onChange={(v) => setForm({ ...form, fssai: v })} testid="kyc-fssai" placeholder="10012345678901" />
              <KycInput label="PAN" value={form.pan} onChange={(v) => setForm({ ...form, pan: v })} testid="kyc-pan" placeholder="ABCDE1234F" />
              <KycInput label="Address proof ref" value={form.address_proof} onChange={(v) => setForm({ ...form, address_proof: v })} testid="kyc-addr" placeholder="Electricity bill / Rent deed" />
            </div>
          </Section>

          <Section title="Owner identity">
            <div className="grid sm:grid-cols-2 gap-3">
              <KycInput label="Owner full name" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="kyc-owner-name" />
              <KycInput label="Owner ID proof (Aadhaar/Passport)" value={form.owner_id_proof} onChange={(v) => setForm({ ...form, owner_id_proof: v })} testid="kyc-owner-id" />
            </div>
          </Section>

          <Section title="Payout bank details">
            <div className="grid sm:grid-cols-2 gap-3">
              <KycInput label="Bank account number" value={form.bank_account} onChange={(v) => setForm({ ...form, bank_account: v })} testid="kyc-bank" />
              <KycInput label="IFSC" value={form.ifsc} onChange={(v) => setForm({ ...form, ifsc: v })} testid="kyc-ifsc" placeholder="HDFC0001234" />
            </div>
          </Section>

          <Section title="Reviewer note">
            <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="kyc-note" placeholder="Internal note about verification status" className="w-full bg-[#121418] border border-white/10 rounded-xl px-3 py-2.5 focus:border-[#E5A93C] outline-none text-sm resize-none" />
          </Section>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 justify-end">
          <button onClick={() => save("pending")} disabled={saving} className="text-xs px-4 py-2 rounded-full border border-white/15 hover:bg-white/5" data-testid="kyc-save">Save</button>
          <button onClick={() => save("rejected")} disabled={saving} className="text-xs px-4 py-2 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="kyc-reject">Reject</button>
          <button onClick={() => save("verified")} disabled={saving} className="text-xs gold-btn rounded-full px-4 py-2 font-semibold inline-flex items-center gap-1" data-testid="kyc-verify">
            <CheckCircle2 className="w-3.5 h-3.5" /> Verify & approve
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 mb-2">{title}</div>
      {children}
    </div>
  );
}

function KycInput({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-xs text-white/50">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className="w-full bg-[#121418] border border-white/10 rounded-xl px-3 py-2 text-sm focus:border-[#E5A93C] outline-none" />
    </div>
  );
}

function KPI({ label, value, sub, icon }) {
  return (
    <div className="p-3 rounded-2xl border border-white/10 bg-[#121418]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{label}</div>
        <div className="w-6 h-6 rounded-full bg-[#E5A93C]/15 border border-[#E5A93C]/30 flex items-center justify-center text-[#E5A93C]">{icon}</div>
      </div>
      <div className="font-display text-2xl mt-1">{value}</div>
      {sub && <div className="text-[10px] text-white/50">{sub}</div>}
    </div>
  );
}

function Tab({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm transition-colors border ${active ? "bg-[#E5A93C] text-black border-[#E5A93C] font-semibold" : "border-white/10 hover:bg-white/5 text-white/70"}`}>{label}</button>
  );
}

function RestaurantForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", slug: "", cuisine: "", address: "",
    owner_email: "", owner_password: "", owner_name: "", subscription_tier: "Pro",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/platform/restaurants", form);
      toast.success(`Onboarded ${form.name}`);
      onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg bg-[#0F1115] rounded-3xl border border-white/10 p-6 max-h-[90vh] overflow-y-auto" data-testid="restaurant-form">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl">Onboard restaurant</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full glass-light flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="rf-name" />
          <Input label="Slug (sub-domain)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/\s/g, "-") })} testid="rf-slug" />
          <CuisineTagInput label="Cuisines" value={form.cuisine} onChange={(v) => setForm({ ...form, cuisine: v })} testid="rf-cuisine" dark />
          <Input label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="rf-addr" />
          <div className="h-px bg-white/10 my-2" />
          <Input label="Owner name" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="rf-oname" />
          <Input label="Owner email" type="email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} testid="rf-oemail" />
          <Input label="Owner password" type="password" value={form.owner_password} onChange={(v) => setForm({ ...form, owner_password: v })} testid="rf-opass" />
          <div>
            <label className="text-xs text-white/50">Subscription tier</label>
            <select value={form.subscription_tier} onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })} className="w-full bg-[#121418] border border-white/10 rounded-xl px-3 py-2.5" data-testid="rf-tier">
              <option>Lite</option><option>Standard</option><option>Pro</option><option>Ultra</option>
            </select>
            <p className="text-[10px] text-white/40 mt-1">AI Sommelier is enabled only on the Pro tier and above.</p>
          </div>
        </div>
        <button type="submit" disabled={loading} className="w-full mt-6 gold-btn rounded-full py-3 font-semibold disabled:opacity-50" data-testid="rf-submit">
          {loading ? "Creating…" : "Onboard & create owner"}
        </button>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, required = true }) {
  return (
    <div>
      <label className="text-xs text-white/50">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required} className="w-full bg-[#121418] border border-white/10 rounded-xl px-3 py-2.5 focus:border-[#E5A93C] focus:ring-1 focus:ring-[#E5A93C] outline-none transition" />
    </div>
  );
}

function EditRestaurantModal({ rid, onClose, onSaved }) {
  const [resto, setResto] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform/restaurants");
        const r = (data || []).find((x) => x.id === rid);
        if (!r) { toast.error("Restaurant not found"); onClose(); return; }
        setResto(r);
        setForm({
          name: r.name || "",
          cuisine: r.cuisine || "",
          address: r.address || "",
          tagline: r.tagline || "",
          subscription_tier: r.subscription_tier || "Lite",
          subscription_active: r.subscription_active !== false,
          assistant_enabled: r.assistant_enabled !== false,
          owner_email: "",
          owner_name: "",
          owner_password: "",
        });
      } catch { toast.error("Could not load"); onClose(); }
    })();
  }, [rid, onClose]);

  if (!form || !resto) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, cuisine: form.cuisine, address: form.address, tagline: form.tagline,
        subscription_tier: form.subscription_tier, subscription_active: form.subscription_active,
        assistant_enabled: form.assistant_enabled,
      };
      if (form.owner_email) payload.owner_email = form.owner_email;
      if (form.owner_name) payload.owner_name = form.owner_name;
      if (form.owner_password) payload.owner_password = form.owner_password;
      await api.put(`/platform/restaurants/${rid}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg bg-[#0F1115] rounded-3xl border border-white/10 p-6 max-h-[90vh] overflow-y-auto" data-testid="edit-restaurant-form">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Edit onboarding</div>
            <h3 className="font-display text-2xl">{resto.name}</h3>
            <div className="text-xs text-white/40 font-mono">/r/{resto.slug} · slug is immutable</div>
          </div>
          <button type="button" onClick={onClose} data-testid="edit-close" className="w-9 h-9 rounded-full glass-light flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <Input label="Restaurant name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ef-name" />
          <CuisineTagInput label="Cuisines" value={form.cuisine} onChange={(v) => setForm({ ...form, cuisine: v })} testid="ef-cuisine" dark />
          <Input label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="ef-addr" required={false} />
          <Input label="Tagline (optional)" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="ef-tagline" required={false} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50">Subscription tier</label>
              <select value={form.subscription_tier} onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })} className="w-full bg-[#121418] border border-white/10 rounded-xl px-3 py-2.5" data-testid="ef-tier">
                <option>Lite</option><option>Standard</option><option>Pro</option><option>Ultra</option>
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6 text-sm cursor-pointer">
              <input type="checkbox" checked={form.subscription_active} onChange={(e) => setForm({ ...form, subscription_active: e.target.checked })} data-testid="ef-active" />
              <span>Subscription active</span>
            </label>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-xl cursor-pointer" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <input type="checkbox" checked={form.assistant_enabled && (form.subscription_tier === "Pro" || form.subscription_tier === "Ultra")} disabled={form.subscription_tier !== "Pro" && form.subscription_tier !== "Ultra"} onChange={(e) => setForm({ ...form, assistant_enabled: e.target.checked })} data-testid="ef-assistant" />
            <div>
              <div className="font-display text-base">AI Sommelier</div>
              <div className="text-xs text-white/60">{(form.subscription_tier === "Pro" || form.subscription_tier === "Ultra") ? "Available on this tenant. Toggle to enable or disable for guests." : "Pro tier or higher required — upgrade above to enable."}</div>
            </div>
          </label>

          <div className="h-px bg-white/10 my-2" />
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Owner credentials (leave blank to keep current)</div>
          <Input label="Owner name" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="ef-oname" required={false} />
          <Input label="Owner email" type="email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} testid="ef-oemail" required={false} />
          <Input label="Reset password (optional)" type="password" value={form.owner_password} onChange={(v) => setForm({ ...form, owner_password: v })} testid="ef-opass" required={false} />
        </div>
        <button type="submit" disabled={saving} data-testid="ef-submit" className="w-full mt-6 gold-btn rounded-full py-3 font-semibold disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}
